import React from 'react';
import { Box, Spinner } from '@razorpay/blade/components';
import { WizardInfoProvider } from 'components/WizardViews/providers';
import { WizardHeader, WizardWrapper } from 'components/WizardViews/components';
import { useUserId } from '../BankAccountDetails/hooks/useUserId';
import { StatutoryContributionsForm } from './StatutoryContributionsForm';
import { useQuery } from 'react-query';
import employeeOnboarding from 'api/employeeOnboarding';
import ErrorMessage from 'components/ui/ErrorMessage';
import { PortalStatus, StatutoryContributionsFormData } from './types';
import { GET_STATUTORY_CONTRIBUTIONS_DATA, initialStatutoryContributionsData } from './data';
import { ContractorStatutoryContributionsForm } from './ContractorStatutoryContributionsForm';
import { routePaths } from 'components/Routes/data';
import { useSearchParams } from 'hooks/useSearchParams';
import { redirectSafely } from 'utils/Urls';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';

export const EditStatutoryContributions = () => {
  const userId = useUserId();

  const { urlParams } = useSearchParams();
  const redirect = urlParams.get('redirect');

  const currentOrganization = useReduxSelector(loggedInUserSelectors.currentOrganization);
  const {
    isPf: canEnablePf,
    isEsi: canEnableEsi,
    isLwf: canEnableLwf,
    isPt: canEnablePt,
  } = currentOrganization || {};

  const { isLoading, isFetching, isError, data } = useQuery({
    queryKey: [GET_STATUTORY_CONTRIBUTIONS_DATA, userId],
    queryFn: () => {
      if (userId) {
        return employeeOnboarding.fetchEmployeeStatutorySettings(userId);
      }
      throw new Error('User ID is required');
    },
    refetchOnMount: true,
    select: (
      statutorySettings,
    ): StatutoryContributionsFormData & { employeeType: 'employee' | 'contractor' } => {
      return {
        pan: statutorySettings?.tds_settings.pan ?? initialStatutoryContributionsData.pan,
        aadharNumber:
          statutorySettings?.tds_settings.aadhaar_number ??
          initialStatutoryContributionsData.aadharNumber,

        isPfEnabled:
          canEnablePf === false
            ? false
            : statutorySettings?.pf_settings.is_pf_enabled ??
              initialStatutoryContributionsData.isPfEnabled,
        pfStatus: (statutorySettings?.pf_settings.pf_status
          ? [statutorySettings.pf_settings.pf_status]
          : initialStatutoryContributionsData.pfStatus) as PortalStatus[],
        pfUan: statutorySettings?.pf_settings.pf_uan ?? initialStatutoryContributionsData.pfUan,
        isAadhaarLinkedToUan:
          statutorySettings?.pf_settings.is_aadhaar_linked_to_uan ??
          initialStatutoryContributionsData.isAadhaarLinkedToUan,
        includeEmployerContributionInCtc:
          statutorySettings?.pf_settings.include_employer_contribution_in_ctc ??
          initialStatutoryContributionsData.includeEmployerContributionInCtc,
        contributeToEps:
          statutorySettings?.pf_settings.contribute_to_eps ??
          initialStatutoryContributionsData.contributeToEps,
        pfWagesCalculationType: statutorySettings?.pf_settings.pf_wages_calculation_type
          ? [statutorySettings.pf_settings.pf_wages_calculation_type]
          : initialStatutoryContributionsData.pfWagesCalculationType,

        isEsicEnabled:
          canEnableEsi === false
            ? false
            : statutorySettings?.esic_settings.is_esic_enabled ??
              initialStatutoryContributionsData.isEsicEnabled,
        esicStatus: (statutorySettings?.esic_settings.esic_status
          ? [statutorySettings.esic_settings.esic_status]
          : initialStatutoryContributionsData.esicStatus) as PortalStatus[],
        esicIpNumber:
          statutorySettings?.esic_settings.esic_ip_number ??
          initialStatutoryContributionsData.esicIpNumber,

        isLwfEnabled:
          canEnableLwf === false
            ? false
            : statutorySettings?.lwf_settings.is_lwf_enabled ??
              initialStatutoryContributionsData.isLwfEnabled,
        lwfLocation: statutorySettings?.lwf_settings.lwf_location
          ? [statutorySettings.lwf_settings.lwf_location.toString()]
          : initialStatutoryContributionsData.lwfLocation,

        isPtEnabled:
          canEnablePt === false
            ? false
            : statutorySettings?.pt_settings.is_pt_enabled ??
              initialStatutoryContributionsData.isPtEnabled,
        ptState: statutorySettings?.pt_settings.pt_state
          ? [statutorySettings.pt_settings.pt_state.toString()]
          : initialStatutoryContributionsData.ptState,

        isNpsEnabled:
          statutorySettings?.nps_settings.is_nps_enabled ??
          initialStatutoryContributionsData.isNpsEnabled,
        npsPranNumber:
          statutorySettings?.nps_settings.nps_pran_number ??
          initialStatutoryContributionsData.npsPranNumber,
        employeeType: statutorySettings?.meta_data?.employment_type ?? 'employee',
      };
    },
  });

  const handleCloseClick = () => {
    if (redirect) {
      redirectSafely(redirect);
    } else {
      window.location.href = `${routePaths.employeeDetails.root}${userId}`;
    }
  };

  if (isLoading || isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Spinner accessibilityLabel="Loading statutory settings" />
      </Box>
    );
  }

  if (isError || !data) {
    return <ErrorMessage title="Error loading statutory settings" />;
  }

  const { employeeType, ...formData } = data;

  return (
    <WizardInfoProvider initialStep={1} totalSteps={1}>
      <WizardWrapper>
        <WizardHeader
          heading="Statutory"
          subHeading="Contributions"
          onCloseClick={handleCloseClick}
        />
        <Box width="100%" flex={1} overflowY="auto" height="100%">
          {employeeType === 'employee' ? (
            <StatutoryContributionsForm formInitialData={formData} onClose={handleCloseClick} />
          ) : (
            <ContractorStatutoryContributionsForm
              formInitialData={formData}
              onClose={handleCloseClick}
            />
          )}
        </Box>
      </WizardWrapper>
    </WizardInfoProvider>
  );
};
